import { KeyboardEvent as ReactKeyboardEvent, MouseEvent } from "react";

import { css } from "@compiled/react";
import { useFragment } from "react-relay";
import { graphql } from "relay-runtime";

import { media } from "@atlaskit/primitives";
import { ProductKeys } from "@atlassiansox/asc-stargate";

import { templateDetailsSectionContentfulRelayFragment$key } from "@/__generated_contentful__/templateDetailsSectionContentfulRelayFragment.graphql";
import { useSiteSelectorContext } from "@/components/site-selector-modal";

import { TemplateCtaButtons } from "../../../template-details-page/template-cta-buttons";
import { TemplateDescription } from "../../components/contentful-relay-components/template-description-contentful-relay";
import { TemplatePreview } from "../../components/contentful-relay-components/template-preview-contentful-relay";
import { TemplateRelatedTemplates } from "../../components/contentful-relay-components/template-related-templates-contentful-relay";

import { TemplateTabbedSection } from "./template-tabbed-section-contentful-relay";

interface TemplateDetailsSectionProps {
    template: templateDetailsSectionContentfulRelayFragment$key;
}

const WHITEBOARD_TEMPLATE_ID = "1HlskYfRNciSnQ02SDhCPs";

const templateDetailsSectionStyles = css({
    [media.below.sm]: {
        height: "auto"
    }
});

const ctaButtonContainerStyles = css({
    display: "none",
    [media.below.sm]: {
        display: "block",
        marginBlockStart: "64px"
    }
});

const previewSectionStyles = css({
    cursor: "pointer"
});

const TemplateDetailsSectionFragment = graphql`
    fragment templateDetailsSectionContentfulRelayFragment on ProductTemplate {
        templateNameExternal
        pluginModuleKey
        templateTypeCollection {
            items {
                sys {
                    id
                }
                templateTypeNameExternal
            }
        }
        ...templatePreviewContentfulRelayFragment
        ...templateDescriptionContentfulRelayFragment
        ...templateTabbedSectionContentfulRelayFragment
        ...templateRelatedTemplatesContentfulRelayFragment
    }
`;

export const TemplateDetailsSection = ({
    template: templateRef
}: TemplateDetailsSectionProps) => {
    const template = useFragment(TemplateDetailsSectionFragment, templateRef);
    const { pluginModuleKey, templateNameExternal, templateTypeCollection } =
        template;
    const { openModalForTemplate } = useSiteSelectorContext();

    const buttonUseTemplateClicked = () => {
        openModalForTemplate({
            templateId: pluginModuleKey ?? "",
            templateName: templateNameExternal ?? "",
            productKey: ProductKeys.CONFLUENCE
        });
    };

    const navigateToSignupClick = (event: MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        event.preventDefault();
        buttonUseTemplateClicked();
    };

    const navigateToSignupKeyDown = (event: ReactKeyboardEvent) => {
        if (event.code === "Space" || event.code === "Enter") {
            event.stopPropagation();
            event.preventDefault();
            buttonUseTemplateClicked();
        }
    };

    const isWhiteBoardTemplate =
        templateTypeCollection?.items?.[0]?.sys?.id === WHITEBOARD_TEMPLATE_ID;

    return (
        <div
            css={templateDetailsSectionStyles}
            data-testid="TemplateDetailsSection"
        >
            <div
                onClick={
                    isWhiteBoardTemplate ? undefined : navigateToSignupClick
                }
                onKeyDown={
                    isWhiteBoardTemplate ? undefined : navigateToSignupKeyDown
                }
                css={!isWhiteBoardTemplate && previewSectionStyles}
                role="img"
                aria-label="Template preview"
                // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                tabIndex={0}
            >
                <TemplatePreview template={template} />
            </div>

            <TemplateDescription template={template} />

            <TemplateTabbedSection template={template} />
            <div css={ctaButtonContainerStyles}>
                <TemplateCtaButtons
                    name={templateNameExternal ?? ""}
                    pluginModuleKey={pluginModuleKey ?? ""}
                />
            </div>
            <TemplateRelatedTemplates template={template} />
        </div>
    );
};
